// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseToolbar, Loading } from '~/components/common';
import { ActivityDetailModal, BaseTimeLine } from '~/components/module/activity';
// Others
import { IActivity } from '~/utils/interface/activity';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { LoadingSizeEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { IParamDetailClient, IQueryDetailClient } from '~/utils/interface/client';
import { getDetailClient } from '~/thunks/client/clientThunk';
import { IBaseResponse, IListDataResponse } from '~/utils/interface/base';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons
import styles from './HistoryClientDetailTab.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const HistoryClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activityList, setActivityList] = useState<IActivity[]>([]);
  const [currentPageActivity, setCurrentPageActivity] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMoreActivity, setHasMoreActivity] = useState<boolean>(true);
  const [activitySelected, setActivitySelected] = useState<IActivity | null>(null);
  const [isShowActivityDetail, setIsShowActivityDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!clientId) return;

    const params: IParamDetailClient = {
      clientId: clientId,
      type: TypeDetailClientEnum.HISTORY,
      query: {
        page: currentPageActivity,
        limit: DEFAULT_LIMIT_PAGE,
      },
    };

    handleGetListActivityClient(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageActivity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListActivityClient = (params: IParamDetailClient) => {
    if (currentPageActivity === DEFAULT_CURRENT_PAGE) {
      setIsLoading(true);
    }

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IActivity[]>>;

        if (!data) return;

        const { responses } = data;

        if (!responses || responses?.length === 0) {
          setHasMoreActivity(false);

          return;
        }

        setActivityList((prev) => {
          const newData = responses?.filter((data) => !prev?.some((oldActivity) => oldActivity?.id === data?.id));

          const updateList = [...prev, ...newData];

          setHasMoreActivity(true);

          return updateList;
        });
      })
      .catch((_error) => {
        setHasMoreActivity(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLoadMore = () => {
    setCurrentPageActivity((prev) => prev + 1);
  };

  const handleViewDetail = (activity: IActivity) => {
    setIsShowActivityDetail(true);
    setActivitySelected(activity);
  };

  const handleCloseActivityDetail = () => {
    setIsShowActivityDetail(false);
    setActivitySelected(null);
  };
  //#endregion Handle Function

  return (
    <Box id='taskClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('history_title')} description={t('history_description')} />
      </Stack>

      {!isLoading &&
        (activityList?.length > 0 ? (
          <BaseTimeLine
            data={activityList}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreActivity}
            onView={handleViewDetail}
            loadMoreSize={LoadingSizeEnum.MEDIUM}
          />
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        ))}

      <ActivityDetailModal
        isOpen={isShowActivityDetail}
        {...(activitySelected && { activity: activitySelected })}
        onClose={handleCloseActivityDetail}
      />

      {isLoading && <Loading />}
    </Box>
  );
};

export default HistoryClientDetailTab;
